import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';
import { API_BASE_URL, type AuthSessionHandler } from 'shared';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

interface BackendUser {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  role: string;
}

interface AuthResponse {
  accessToken: string;
  user: BackendUser;
}

const TOKEN_KEY = 'kota_token';
const USER_KEY = 'kota_user';

@Injectable({ providedIn: 'root' })
export class AuthService implements AuthSessionHandler {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  private readonly _token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  private readonly _user = signal<AuthUser | null>(this.loadUser());

  readonly currentUser = this._user.asReadonly();
  readonly isLoggedIn = computed(() => !!this._token());

  login(email: string, password: string): Observable<void> {
    return this.http
      .post<AuthResponse>(`${this.base}/auth/login`, { email, password }, { withCredentials: true })
      .pipe(
        tap(({ accessToken, user }) => this.saveSession(accessToken, user)),
        map(() => void 0)
      );
  }

  sendVerificationCode(email: string, recaptchaToken: string): Observable<void> {
    return this.http.post<void>(`${this.base}/auth/send-code`, { email, recaptchaToken });
  }

  register(firstName: string, lastName: string, email: string, password: string, code: string): Observable<void> {
    return this.http
      .post<AuthResponse>(
        `${this.base}/auth/registro`,
        { nombre: firstName, apellido: lastName, email, password, verification_code: code },
        { withCredentials: true }
      )
      .pipe(
        tap(({ accessToken, user }) => this.saveSession(accessToken, user)),
        map(() => void 0)
      );
  }

  logout(): void {
    this.http.post(`${this.base}/auth/logout`, {}, { withCredentials: true }).subscribe();
    this.clearSession();
  }

  /** Actualiza el token tras refresh; usado por ErrorInterceptor. */
  setAccessToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    this._token.set(token);
  }

  /** Cierra sesión local sin redirigir (p. ej. token expirado en una petición del carrito). */
  invalidate(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._token.set(null);
    this._user.set(null);
  }

  getToken(): string | null {
    return this._token();
  }

  updateUser(partial: Partial<AuthUser>): void {
    const updated = { ...this._user()!, ...partial };
    localStorage.setItem(USER_KEY, JSON.stringify(updated));
    this._user.set(updated);
  }

  private saveSession(token: string, backendUser: BackendUser): void {
    const user: AuthUser = {
      id: String(backendUser.id),
      name: `${backendUser.nombre} ${backendUser.apellido}`.trim(),
      email: backendUser.email
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this._token.set(token);
    this._user.set(user);
    // Lazy import para evitar dependencia circular con CartService
    import('./cart.service').then(({ CartService }) => {
      this.injector.get(CartService).onLogin();
    });
    void this.router.navigate(['/']);
  }

  private clearSession(): void {
    this.invalidate();
    void this.router.navigate(['/login']);
  }

  private loadUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }
}
